import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { DirectionsCar as CarIcon } from '@mui/icons-material';
import { API_SERVER_URL } from '../resources/constants';

/**
 * Fotografie vozidla na poplachovem displeji.
 *
 * Kiosk bezi bez obsluhy — kdyz se obrazek pri prvnim nacteni nestahne (vypadek site,
 * restart backendu, timeout u velke fotky), prohlizec u <img> se stejnym src sam
 * opakovany pokus NEDELA a karta uz zustane bez fotky az do reloadu stranky.
 * Proto si tady pokusy rezirujeme sami: pri chybe pockame a zmenime src (cache-busting
 * parametr `r`), coz prohlizec vyhodnoti jako novy zdroj a stahne ho znovu.
 *
 * Pokusy jsou zamerne omezene — u vozidla, ktere fotku opravdu nema nebo je
 * poskozena, nema smysl kiosk nechat donekonecna klepat na backend. Po vycerpani
 * pokusu se zobrazi stejny placeholder jako u vozidla bez fotky, aby poplachova
 * obrazovka nikdy neukazovala chybovy stav.
 */

/** Prodleva pred 1., 2. a 3. opakovanym pokusem. Delka pole = pocet pokusu navic. */
const PRODLEVY_MS = [2000, 5000, 10000];

const DIAG = '[TechnikaFoto]';

const fotoUrl = (id: number, deviceToken?: string, pokus?: number): string => {
    const params = new URLSearchParams();
    if (deviceToken) params.set('deviceToken', deviceToken);
    // `r` se pridava az od prvniho opakovani, aby se prvni (a v praxi jediny)
    // pozadavek dal normalne cachovat.
    if (pokus) params.set('r', String(pokus));
    const qs = params.toString();
    return `${API_SERVER_URL}/api/technika/${id}/fotografie${qs ? `?${qs}` : ''}`;
};

interface TechnikaFotoProps {
    technikaId: number;
    /** Z ciselniku techniky — false = vozidlo fotku vubec nema, rovnou placeholder. */
    maFotografii: boolean;
    alt: string;
    deviceToken?: string;
    /** Rozmery ramecku, sdilene pro fotku i placeholder (vyska/sirka/pomer stran). */
    sx?: SxProps<Theme>;
    objectFit?: 'cover' | 'contain';
    ikonaFontSize?: number;
}

const TechnikaFoto: React.FC<TechnikaFotoProps> = ({
    technikaId, maFotografii, alt, deviceToken,
    sx, objectFit = 'cover', ikonaFontSize = 64,
}) => {
    const [pokus, setPokus] = useState(0);
    const [vzdano, setVzdano] = useState(false);
    /** true = posledni pokus selhal a cekame na dalsi; misto rozbitehe obrazku ukazujeme placeholder. */
    const [chyba, setChyba] = useState(false);
    const pokusRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const zrusTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    };

    // Jine vozidlo (nebo jiny token) = citac pokusu od zacatku.
    useEffect(() => {
        zrusTimer();
        pokusRef.current = 0;
        setPokus(0);
        setVzdano(false);
        setChyba(false);
    }, [technikaId, deviceToken]);

    useEffect(() => zrusTimer, []);

    const handleError = useCallback(() => {
        // Skryt rozbity obrazek driv, nez ho prohlizec staci ukazat jako alt text
        // s ikonou rozbiteho obrazku — na poplachove obrazovce chceme placeholder.
        setChyba(true);
        if (pokusRef.current >= PRODLEVY_MS.length) {
            console.warn(`${DIAG} technika=${technikaId}: fotka se nenacetla ani po ${PRODLEVY_MS.length} opakovanich, zobrazuji placeholder`);
            setVzdano(true);
            return;
        }
        const prodleva = PRODLEVY_MS[pokusRef.current];
        pokusRef.current += 1;
        const dalsiPokus = pokusRef.current;
        console.warn(`${DIAG} technika=${technikaId}: fotka se nenacetla, opakuji pokus ${dalsiPokus}/${PRODLEVY_MS.length} za ${prodleva} ms`);
        zrusTimer();
        timerRef.current = setTimeout(() => setPokus(dalsiPokus), prodleva);
    }, [technikaId]);

    const handleLoad = useCallback(() => {
        if (pokusRef.current > 0) {
            console.info(`${DIAG} technika=${technikaId}: fotka nactena az na ${pokusRef.current}. opakovany pokus`);
        }
        setChyba(false);
    }, [technikaId]);

    const ramecekSx: SxProps<Theme> = {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 1, flex: '0 0 auto', overflow: 'hidden',
        ...sx,
    };

    if (!maFotografii || vzdano) {
        return (
            <Box sx={{ ...ramecekSx, bgcolor: 'action.hover' }}>
                <CarIcon sx={{ fontSize: ikonaFontSize, color: 'text.disabled' }} />
            </Box>
        );
    }

    // Obrazek zustava v DOM i behem cekani na dalsi pokus (display:none stahovani
    // nebrani), jen se misto nej ukazuje placeholder — karta tak nikdy neproblikne
    // rozbitym obrazkem a nemeni rozmery.
    return (
        <Box sx={{ ...ramecekSx, bgcolor: chyba ? 'action.hover' : 'transparent' }}>
            <Box
                component="img"
                src={fotoUrl(technikaId, deviceToken, pokus)}
                alt={alt}
                onError={handleError}
                onLoad={handleLoad}
                sx={{
                    width: '100%', height: '100%', objectFit,
                    display: chyba ? 'none' : 'block',
                }}
            />
            {chyba && <CarIcon sx={{ fontSize: ikonaFontSize, color: 'text.disabled' }} />}
        </Box>
    );
};

export default React.memo(TechnikaFoto);
